import { LevelSim, SimEventKind, wildPetSkin } from "@highmarch/domain";
import { audio } from "../audio/audio";
import type { SceneRig } from "../render/scene";
import { SKIN_LOOKS } from "../render/skins/looks";
import type { PetView } from "../render/views/petView";
import { petLook } from "../render/views/petPresent";
import type { WildPetView } from "../render/views/wildPetView";
import type { GuideSignal } from "../ui/guide/guideTypes";
import type { HudView } from "../ui/hud";
import { BossBar } from "../ui/bossBar";
import { setPetPortrait } from "../ui/pets/petArt";
import { PetHudChip, woofAt } from "../ui/pets/petHudChip";
import { petLink } from "../ui/pets/petLink";
import { PetCatchCard, PetEscapeNote, WildPetBanner, WildPetTag } from "../ui/pets/petMomentUi";
import { chaseWords, chipNote, petShout } from "../ui/pets/petWords";

/** How long the chase's "CATCH THE MUSTER PUP!" banner holds (ms). */
const CHASE_BANNER_MS = 2200;
/** The wild pet's stingers (the rare spawn's and the boss's) a little higher: a pup, not a warlord. */
const WILD_CUES = { pitch: 1.2 } as const;
/** The TAP! tag rides this high over the romping pet's feet (world units at its scale 1). */
const TAG_HEAD = 0.26;

/**
 * The pet's part in the level's HUD and moments (Pets v2, docs/PETS.md; presentation only, it never writes the sim):
 * the equipped pet's chip on the thumb cluster (its level; a tap glides the camera to it; it bounces on each bark) and
 * the bark's "WOOF!"; and the wild pet's moments — the banner as it appears (its rare coat named), the TAP! tag over it
 * while it romps, the chase's banner and its boss bar (the pup's portrait) as it comes down the road, the catch card
 * (what the catch paid: a new pet, a level or gold, a new coat) and the escape note. The catch is settled the moment
 * it lands (petLink.settle: features/pets.ts → app Pets.settle, idempotent per run), as is the treat when it appears;
 * the guide hears `wildPetAppeared` for Captain Bram's first line (scripts/pets.ts); his cheer is on the catch card.
 */
export class LevelPet {
  private readonly chip: PetHudChip | null = null;
  /** The note off the chip when the pet's pulse goes off (petWords.ts chipNote). */
  private readonly pulseNote: string = "";
  private readonly banner: WildPetBanner;
  private readonly tag: WildPetTag;
  private readonly card: PetCatchCard;
  private readonly escape: PetEscapeNote;
  private chaseId = 0;
  private undressTimer = 0;

  constructor(private readonly sim: LevelSim, pet: PetView, private readonly wild: WildPetView, private readonly hud: HudView, private readonly rig: SceneRig, private readonly uiRoot: HTMLElement,
              private readonly raise: (s: GuideSignal) => void = () => {}) {
    const run = sim.mods.pet, look = petLook();
    this.banner = new WildPetBanner(uiRoot);
    this.tag = new WildPetTag(uiRoot);
    this.card = new PetCatchCard(uiRoot, () => this.chip?.root.getBoundingClientRect() ?? hud.root.getBoundingClientRect());
    this.escape = new PetEscapeNote(uiRoot);
    if (run && look.hud) {
      const name = sim.balance.pets.list.find((p) => p.id === run.id)?.name ?? "Pet";
      this.chip = new PetHudChip(run.id, name, run.level, () => { const at = pet.at; if (at) rig.iso.glide(at.x, at.z); });
      hud.addClusterChip(this.chip.root);
    }
    const runPet = run ? sim.balance.pets.list.find((p) => p.id === run.id) ?? null : null;
    this.pulseNote = chipNote(runPet, "pulse");
    pet.onBark = (mouth, why) => {
      if (why === "muster") this.chip?.bark(chipNote(runPet, "muster"));     // a pulse's note comes with its PetBarked
      if (!look.burst) return;
      const v = rig.iso.worldToViewport(mouth.x, mouth.y, mouth.z);
      woofAt(uiRoot, v.x, v.y, petShout(run?.id ?? ""));
    };
  }

  /** The tick's (or a command's) new events: the wild pet's beats. */
  observe(from: number): void {
    const events = this.sim.events.all();
    for (let i = from; i < events.length; i++) {
      const ev = events[i];
      switch (ev.kind) {
        case SimEventKind.WildPetAppeared: this.appeared(); break;
        case SimEventKind.WildPetEngaged: this.engaged(ev.a); break;
        case SimEventKind.WildPetCaught: this.caught(); break;
        case SimEventKind.WildPetEscaped: this.escaped(); break;
        case SimEventKind.PetBarked: this.chip?.bark(this.pulseNote); break;
        case SimEventKind.BossSpawned: this.undress(); break;      // the level's own boss takes the bar back
        default: break;
      }
    }
  }

  private get petData() { const id = this.sim.wildPet?.petId ?? ""; return this.sim.balance.pets.list.find((p) => p.id === id) ?? null; }
  private skinName(skin: string | null): string | null { return skin ? SKIN_LOOKS[skin]?.name ?? null : null; }

  private appeared(): void {
    const pet = this.petData;
    if (!pet) return;
    petLink.settle(this.sim);                                // the treat armed for it is used up now
    const skin = wildPetSkin(this.sim);
    this.banner.show(pet, skin, this.skinName(skin));
    audio.play("rareSpawn", 0, WILD_CUES.pitch);
    this.raise("wildPetAppeared");
  }

  private engaged(enemyId: number): void {
    const pet = this.petData;
    if (!pet) return;
    this.chaseId = enemyId;
    this.banner.hide();
    const w = chaseWords(pet);
    this.hud.announce(w.title, "boss", CHASE_BANNER_MS, w.sub);
    audio.play("boss", 0, WILD_CUES.pitch);
    const bar = this.hud.bossBar;
    bar.spawn(this.sim.balance.enemies[this.sim.wildPet?.archetype ?? 0]?.id ?? "", pet.name);
    const img = bar.root.querySelector<HTMLImageElement>(".bb-face img");
    if (img) setPetPortrait(img, pet.id, wildPetSkin(this.sim));
    bar.root.classList.add("petchase");
  }

  private caught(): void {
    const pet = this.petData;
    this.hud.bossBar.death();
    this.chaseId = 0;
    this.undressLater();
    const grant = petLink.settle(this.sim);
    if (pet && grant) this.card.show(grant, pet, this.skinName(grant.skin));
    audio.play("rareKill");
  }

  private escaped(): void {
    const pet = this.petData;
    this.hud.bossBar.gone();
    this.chaseId = 0;
    this.undressLater();
    if (pet) this.escape.show(pet, wildPetSkin(this.sim));
  }

  /** The bar sheds the pup's dress once it has gone (it lingers a moment after a catch). */
  private undressLater(): void { window.clearTimeout(this.undressTimer); this.undressTimer = window.setTimeout(() => { if (this.chaseId === 0) this.undress(); }, BossBar.LingerMs * 2); }
  private undress(): void { window.clearTimeout(this.undressTimer); this.hud.bossBar.root.classList.remove("petchase"); }

  /** Every frame: the chase's bar follows the pup's health; the TAP! tag rides over the romping pet. */
  frame(): void {
    if (this.chaseId !== 0) {
      const e = this.sim.enemySlots.find((x) => x.id === this.chaseId && x.alive);
      if (e) this.hud.bossBar.hp(e.hp.toNumber() / Math.max(1e-6, e.maxHp.toNumber()));
    }
    const at = this.wild.tappable ? this.wild.at : null;
    if (!at) { this.tag.place(null); return; }
    const v = this.rig.iso.worldToViewport(at.x, at.y + TAG_HEAD * petLook().scale, at.z);
    this.tag.place(v);
  }

  dispose(): void { window.clearTimeout(this.undressTimer); this.chip?.dispose(); this.banner.hide(); this.tag.dispose(); this.card.hide(); this.escape.hide(); }
}
